import {catchError} from 'rxjs/operators';
import { Injectable, inject } from '@angular/core';
import {HttpClient} from './http.service';
import {BaseService} from './base.service';
import {Observable} from 'rxjs';
import {DeliveryDate} from '../classes/deliverydate';
import {DeliveryAddress} from '../classes/deliveryaddress';
import {ActiveOrder} from '../classes/active.order';
import {Logger} from '../logging/default-log.service';
import {ConfigurationHelper} from '../helper/configuration.helper';
import {ProductOrderData} from '../classes/product.order.data';

@Injectable()
export class DeliveryService extends BaseService {
	private http = inject(HttpClient);

	constructor() {
		const logger = inject(Logger);

		super(logger);
	}

	getDeliveryAddresses(): Observable<DeliveryAddress[]> {
		return this.http
			.get<DeliveryAddress[]>(ConfigurationHelper.getWebshopUrl('/rest/deliveryaddresses'))
			.pipe(catchError(this.handleError));
	}

	getDeliveryAddressForCode(code: string): Observable<DeliveryAddress> {
		return this.http
			.get<DeliveryAddress>(
				ConfigurationHelper.getWebshopUrl(
					'/rest/deliveryaddress?code=' + encodeURIComponent(code),
				),
			)
			.pipe(catchError(this.handleError));
	}

	/**
	 * Get a DeliveryDate object for the given Date
	 *
	 * @param productData can be null when you don't want to filter the delivery date for a specific product.
	 * @param shipmentDate the shipment date to fetch the delivery date for.
	 * @returns {Observable<DeliveryDate>}
	 */
	getDeliveryDate(productData: ProductOrderData | null, shipmentDate: number): Observable<DeliveryDate> {
		return this.fetchDeliveryDate(this.getProductUrlParams(productData), shipmentDate);
	}

	/**
	 * @returns {Observable<DeliveryDate[]>}
	 * @param productUrlParams the product url params to fetch the delivery dates for.
	 */
	fetchDeliveryDates(productUrlParams: string): Observable<DeliveryDate[]> {
		const url = ConfigurationHelper.getWebshopUrl(`/rest/product/deliverydates?${productUrlParams}`);

		return this.http
			.get<DeliveryDate[]>(url)
			.pipe(catchError(this.handleError));
	}

	/**
	 * @param {Product} productData can be null when you don't want to filter the delivery date for a specific product.
	 * @returns {Observable<DeliveryDate[]>}
	 */
	getDeliveryDates(productData: ProductOrderData | null): Observable<DeliveryDate[]> {
		return this.fetchDeliveryDates(this.getProductUrlParams(productData));
	}

	/**
	 * Get a DeliveryDate object for the given Date
	 *
	 * @param productUrlParams the product url params to fetch the delivery date for.
	 * @param shipmentDate the shipment date to fetch the delivery date for.
	 * @returns {Observable<DeliveryDate>}
	 */
	private fetchDeliveryDate(productUrlParams: string, shipmentDate: number): Observable<DeliveryDate> {
		const url = ConfigurationHelper.getWebshopUrl(`/rest/product/deliverydate?shipmentDate=${shipmentDate}&${productUrlParams}`);

		return this.http
			.get<DeliveryDate>(url)
			.pipe(catchError(this.handleError));
	}

	public getClosingInHours(closingTime: number): number {
		let hours = 0;

		if (closingTime) {
			const now = Date.now();
			const diff = closingTime - now;

			if (diff > 0) {
				hours = Math.abs(diff) / 36e5;
			}
		}

		return hours;
	}

	/**
	 * Check if the given ActiveOrder is still open
	 *
	 * @param activeOrder
	 * @returns {boolean}
	 */
	public isActiveOrderStillOpen(activeOrder: ActiveOrder): boolean {
		// Only check if deliveryDate object is not null
		if (activeOrder.closingTime) {
			return this.getClosingInHours(activeOrder.closingTime) > 0;
		}

		// Otherwise, it's a new order that hasn't come back from the server yet.
		return true;
	}

	public getClosingTimeStringForActiveOrder(
		closingTime: number,
		closingHour: number,
		closingInHours: number,
		dayTranslation: string,
		daysTranslation: string,
		tomorrowTranslation: string,
		closedTranslation: string,
		closedInTranslation: string,
	): string {
		return this.getClosingTimeString(
			closingTime,
			closingHour,
			closingInHours,
			dayTranslation,
			daysTranslation,
			tomorrowTranslation,
			closedTranslation,
			closedInTranslation,
		);
	}

	/**
	 * Tries to format the closing time string which differs between 'closed', '15:00' (an hour in the day),
	 * or the number of days if larger than 24
	 */
	public getClosingTimeString(
		closingTime: number,
		closingHour: number,
		closingInHours: number,
		dayTranslation: string,
		daysTranslation: string,
		tomorrowTranslation: string,
		closedTranslation: string,
		closedInTranslation: string,
	): string {
		if (closingInHours < 1) {
			return closedTranslation;
		}

		if (closingInHours >= 24) {
			const numberOfDays: number = Math.round(closingInHours / 24);
			return (
				closedInTranslation +
				' ' +
				numberOfDays +
				' ' +
				(numberOfDays === 1 ? dayTranslation : daysTranslation)
			);
		}

		if (this.checkClosingTimeToday(closingTime)) {
			// determine exact hour
			return closingHour + ':00';
		}

		return tomorrowTranslation + ' ' + closingHour + ':00';
	}

	private getProductUrlParams(productData: ProductOrderData | null): string {
		if (!productData) {
			return '';
		}

		return `type=${productData.type}&priceListLineNo=${productData.priceListLineNo}&priceListNo=${productData.priceListNo}`;
	}

	/**
	 * Compare given timestamp with today to check if both timestamps are on the same day
	 *
	 * @param closingTime
	 * @returns {boolean}
	 */
	private checkClosingTimeToday(closingTime: number): boolean {
		const currentDate = new Date().setHours(0, 0, 0, 0);
		const closingTimeDate = new Date(closingTime).setHours(0, 0, 0, 0);

		return currentDate === closingTimeDate;
	}
}
